import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Center,
  Divider,
  Heading,
  HStack,
  Image,
  Input,
  Pressable,
  Radio,
  Spinner,
  Text,
  useDisclose,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import { Marker, Polyline } from "@react-google-maps/api";
import decodePolyline from "decode-google-map-polyline";
import shortid from "shortid";
import { useNavigate, useParams } from "react-router-dom";
import { FiMap } from "react-icons/fi";
import colors from "../../../constants/colors";
import Location from "../../../components/location";
import seat from "../../../assets/icons/seat.svg";
import addCircle from "../../../assets/icons/add-circle.svg";
import removeCircle from "../../../assets/icons/remove-circle.svg";
import Map from "../../../components/map";
import routes from "../../../routes";
import mapIcons from "../../../constants/mapIcons";
import Cell from "../../../components/cell";
import styles from "./styles.module.css";
import constants from "../../../constants";
import AddLocation from "../../../components/addLocation";
import {
  getBoardingPoints,
  getDestination,
  getIntermediateDestinations,
} from "../../../utils";
import API from "../../../api";
import { StateContext, StatusContext } from "../../../contexts";
import QuotationFinishedModal from "../../../components/quotationFinishedModal";
import Holder from "../../../components/holder";
import NoResultsModal from "../../../components/noResultsModal";
import Vehicles from "../../../components/vehicles";

const fields = {
  duration: 0,
  distance: 0,
  arrivalDate: "",
  departureDate: "",
};

function AgencyExperienceCreationPage() {
  const params = useParams();
  const navigate = useNavigate();
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const mapContainer = useRef();
  const [map, setMap] = useState();
  const [returnMap, setReturnMap] = useState();
  const [origin, setOrigin] = useState({
    ...constants.location,
    type: "origin",
  });
  const [boardingPoints, setBoardingPoints] = useState([]);
  const [intermediateDestinations, setIntermediateDestinations] = useState([]);
  const [destination, setDestination] = useState({
    ...constants.location,
    type: "destination",
  });
  const [polyline, setPolyline] = useState("");
  const [returnOrigin, setReturnOrigin] = useState({
    ...constants.location,
    isReturn: true,
    type: "origin",
  });
  const [returnIntermediateDestinations, setReturnIntermediateDestinations] =
    useState([]);
  const [returnBoardingPoints, setReturnBoardingPoints] = useState([]);
  const [returnDestination, setReturnDestination] = useState({
    ...constants.location,
    isReturn: true,
    type: "destination",
  });
  const [returnPolyline, setReturnPolyline] = useState("");
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [numberOfPassengers, setNumberOfPassengers] = useState(15);
  const [isLoading, setIsLoading] = useState(Boolean(params?.id));
  const [isSaving, setIsSaving] = useState(false);
  const [finished, setFinished] = useState(false);
  const [quotationExists] = useState(Boolean(params?.id));
  const [holder, setHolder] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const {
    isOpen: isNoResultsModalOpen,
    onOpen: onOpenNoResultsModalOpen,
    onClose: onCloseNoResultsModalOpen,
  } = useDisclose();

  const getQuotation = useCallback(async () => {
    try {
      if (!params.id || !state.jwt) {
        return;
      }
      setIsLoading(true);
      const {
        data: {
          data: {
            itinerary,
            numberOfPassengers: _numberOfPassengers,
            holder: _holder,
          },
        },
      } = await API(state.jwt).get(`/quotations/${params.id}`);
      const _origin = itinerary.find(
        (location) => !location.isReturn && location.type === "origin",
      );
      const _boardingPoints = itinerary.filter(
        (location) => !location.isReturn && location.type === "boardingPoint",
      );
      const _intermediateDestinations = itinerary.filter(
        (location) =>
          !location.isReturn && location.type === "intermediateDestination",
      );
      const _destination = itinerary.find(
        (location) => !location.isReturn && location.type === "destination",
      );
      const _isRoundTrip = itinerary.some((location) => location.isReturn);
      const _returnOrigin = itinerary.find(
        (location) => location.isReturn && location.type === "origin",
      );
      const _returnIntermediateDestinations = itinerary.filter(
        (location) =>
          location.isReturn && location.type === "intermediateDestination",
      );
      const _returnBoardingPoints = itinerary.filter(
        (location) => location.isReturn && location.type === "boardingPoint",
      );
      const _returnDestination = itinerary.find(
        (location) => location.isReturn && location.type === "destination",
      );
      setOrigin(_origin);
      setBoardingPoints(_boardingPoints);
      setIntermediateDestinations(_intermediateDestinations);
      setDestination(_destination);
      setIsRoundTrip(_isRoundTrip);
      if (_isRoundTrip) {
        setReturnOrigin(_returnOrigin);
        setReturnIntermediateDestinations(_returnIntermediateDestinations);
        setReturnBoardingPoints(_returnBoardingPoints);
        setReturnDestination(_returnDestination);
      }
      setNumberOfPassengers(_numberOfPassengers);
      setHolder(_holder);
    } catch (error) {
      if (error?.response?.data?.error?.details?.finished) {
        setFinished(true);
      }
      status.updateErrorMessage(error);
    }
    setIsLoading(false);
  }, [params.id, state.jwt, status]);

  useEffect(() => {
    getQuotation();
  }, [getQuotation]);

  useEffect(() => {
    if (quotationExists && !params.id) {
      setOrigin({ ...constants.location, type: "origin" });
      setBoardingPoints([]);
      setIntermediateDestinations([]);
      setDestination({ ...constants.location, type: "destination" });
      setPolyline("");
      setReturnOrigin({
        ...constants.location,
        isReturn: true,
        type: "origin",
      });
      setReturnIntermediateDestinations([]);
      setReturnBoardingPoints([]);
      setReturnDestination({
        ...constants.location,
        isReturn: true,
        type: "destination",
      });
      setReturnPolyline("");
      setIsRoundTrip(false);
      setNumberOfPassengers(15);
    }
  }, [params, quotationExists]);

  const getRoute = useCallback(async () => {
    try {
      const locations = [
        origin,
        ...boardingPoints,
        ...intermediateDestinations,
        destination,
      ];
      if (locations.some((location) => !location.placeId)) {
        return;
      }
      setIsSaving(true);
      setPolyline("");
      setNoResults(false);
      const response = await new window.google.maps.DirectionsService().route({
        origin: { placeId: origin.placeId },
        destination: { placeId: destination.placeId },
        travelMode: "DRIVING",
        waypoints: [
          ...boardingPoints.map((boardingPoint) => ({
            location: { placeId: boardingPoint.placeId },
          })),
          ...intermediateDestinations.map((intermediateDestination) => ({
            location: { placeId: intermediateDestination.placeId },
          })),
        ],
      });
      const [route] = response.routes;
      const durations = route.legs.map(
        (leg) => Math.ceil(leg.duration.value / 60) * 60,
      );
      const distances = route.legs.map((leg) => Math.ceil(leg.distance.value));
      const _origin = { ...origin };
      _origin.duration = durations.shift();
      _origin.distance = distances.shift();
      const _boardingPoints = getBoardingPoints(
        _origin,
        [...boardingPoints].map((location, index) => ({
          ...location,
          duration: durations.at(index),
          distance: distances.at(index),
        })),
      );
      const _intermediateDestinations = getIntermediateDestinations(
        _origin,
        _boardingPoints,
        [...intermediateDestinations].map((location, index) => ({
          ...location,
          duration: durations.at(_boardingPoints.length + index),
          distance: distances.at(_boardingPoints.length + index),
        })),
      );
      const _destination = getDestination(
        _origin,
        _boardingPoints,
        _intermediateDestinations,
        destination,
      );
      setPolyline(route.overview_polyline);
      setOrigin(_origin);
      setBoardingPoints(_boardingPoints);
      setIntermediateDestinations(_intermediateDestinations);
      setDestination(_destination);
      setIsSaving(false);
    } catch (error) {
      if (error?.code === "ZERO_RESULTS") {
        setNoResults(true);
        onOpenNoResultsModalOpen();
      }
      setIsSaving(false);
    }
  }, [
    origin,
    boardingPoints,
    intermediateDestinations,
    destination,
    onOpenNoResultsModalOpen,
  ]);

  useEffect(() => {
    getRoute();
    setPolyline("");
    if (origin.placeId) {
      setOrigin({ ...origin, ...fields });
    }
    if (destination.placeId) {
      setDestination({ ...destination, ...fields });
    }
    if (boardingPoints.length) {
      setBoardingPoints(
        boardingPoints.map((boardingPoint) => ({
          ...boardingPoint,
          ...fields,
        })),
      );
    }
    if (intermediateDestinations.length) {
      setIntermediateDestinations(
        intermediateDestinations.map((intermediateDestination) => ({
          ...intermediateDestination,
          ...fields,
        })),
      );
    }
    if (isRoundTrip) {
      setReturnPolyline("");
      if (destination.placeId) {
        setReturnOrigin({
          ...destination,
          ...fields,
          isReturn: true,
          type: "origin",
          id: undefined,
        });
      }
      if (origin.placeId) {
        setReturnDestination({
          ...origin,
          ...fields,
          isReturn: true,
          type: "destination",
          id: undefined,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify([
      origin.placeId,
      ...boardingPoints.map((boardingPoint) => boardingPoint.placeId),
      ...intermediateDestinations.map(
        (intermediateDestination) => intermediateDestination.placeId,
      ),
      destination.placeId,
    ]),
  ]);

  useEffect(() => {
    if (map) {
      const locations = [
        origin,
        ...boardingPoints,
        ...intermediateDestinations,
        destination,
      ].filter((location) => location.placeId);
      if (locations.length === 1) {
        const [location] = locations;
        map.panTo({
          lat: location.latitude,
          lng: location.longitude,
        });
      } else if (locations.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        locations.forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
        map.fitBounds(bounds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify([
      origin.placeId,
      ...boardingPoints.map((boardingPoint) => boardingPoint.placeId),
      ...intermediateDestinations.map(
        (intermediateDestination) => intermediateDestination.placeId,
      ),
      destination.placeId,
    ]),
  ]);

  useEffect(() => {
    if (returnMap) {
      const locations = [
        returnOrigin,
        ...returnIntermediateDestinations,
        ...returnBoardingPoints,
        returnDestination,
      ].filter((location) => location.placeId);
      if (locations.length === 1) {
        const [location] = locations;
        returnMap.panTo({
          lat: location.latitude,
          lng: location.longitude,
        });
      } else if (locations.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        locations.forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
        returnMap.fitBounds(bounds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    returnMap,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify([
      returnOrigin.placeId,
      ...returnIntermediateDestinations.map(
        (returnIntermediateDestination) =>
          returnIntermediateDestination.placeId,
      ),
      ...returnBoardingPoints.map(
        (returnBoardingPoint) => returnBoardingPoint.placeId,
      ),
      returnDestination.placeId,
    ]),
  ]);

  useEffect(() => {
    if (isRoundTrip) {
      setReturnOrigin({
        ...destination,
        ...fields,
        isReturn: true,
        type: "origin",
        id: undefined,
      });
      setReturnDestination({
        ...origin,
        ...fields,
        isReturn: true,
        type: "destination",
        id: undefined,
      });
    } else {
      setReturnPolyline("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoundTrip, origin.placeId, destination.placeId]);

  useEffect(() => {
    if (isRoundTrip) {
      const _returnBoardingPoints = boardingPoints
        .map((location) => ({
          ...location,
          ...fields,
          isReturn: true,
          id: undefined,
        }))
        .reverse();
      const _returnIntermediateDestinations = intermediateDestinations
        .map((location) => ({
          ...location,
          ...fields,
          isReturn: true,
          id: undefined,
        }))
        .reverse();
      setReturnBoardingPoints(_returnBoardingPoints);
      setReturnIntermediateDestinations(_returnIntermediateDestinations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoundTrip]);

  const getReturnRoute = useCallback(async () => {
    try {
      const locations = [
        returnOrigin,
        ...returnIntermediateDestinations,
        ...returnBoardingPoints,
        returnDestination,
      ];
      if (locations.some((location) => !location.placeId)) {
        return;
      }
      setIsSaving(true);
      setReturnPolyline("");
      const response = await new window.google.maps.DirectionsService().route({
        origin: { placeId: returnOrigin.placeId },
        destination: { placeId: returnDestination.placeId },
        travelMode: "DRIVING",
        waypoints: [
          ...returnIntermediateDestinations.map(
            (returnIntermediateDestination) => ({
              location: { placeId: returnIntermediateDestination.placeId },
            }),
          ),
          ...returnBoardingPoints.map((returnBoardingPoint) => ({
            location: { placeId: returnBoardingPoint.placeId },
          })),
        ],
      });
      const [route] = response.routes;
      const durations = route.legs.map(
        (leg) => Math.ceil(leg.duration.value / 60) * 60,
      );
      const distances = route.legs.map((leg) => Math.ceil(leg.distance.value));
      const _returnOrigin = { ...returnOrigin };
      _returnOrigin.duration = durations.shift();
      _returnOrigin.distance = distances.shift();
      const _returnIntermediateDestinations = getBoardingPoints(
        _returnOrigin,
        [...returnIntermediateDestinations].map((location, index) => ({
          ...location,
          duration: durations.at(index),
          distance: distances.at(index),
        })),
      );
      const _returnBoardingPoints = getIntermediateDestinations(
        _returnOrigin,
        _returnIntermediateDestinations,
        [...returnBoardingPoints].map((location, index) => ({
          ...location,
          duration: durations.at(
            _returnIntermediateDestinations.length + index,
          ),
          distance: distances.at(
            _returnIntermediateDestinations.length + index,
          ),
        })),
      );
      const _returnDestination = getDestination(
        _returnOrigin,
        _returnIntermediateDestinations,
        _returnBoardingPoints,
        returnDestination,
      );
      setReturnPolyline(route.overview_polyline);
      setReturnOrigin(_returnOrigin);
      setReturnIntermediateDestinations(_returnIntermediateDestinations);
      setReturnBoardingPoints(_returnBoardingPoints);
      setReturnDestination(_returnDestination);
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
    }
  }, [
    returnOrigin,
    returnIntermediateDestinations,
    returnBoardingPoints,
    returnDestination,
  ]);

  useEffect(() => {
    if (isRoundTrip) {
      const locations = [
        origin,
        ...boardingPoints,
        ...intermediateDestinations,
        destination,
      ];
      if (locations.every((location) => location.placeId)) {
        getReturnRoute(destination);
      }
      if (destination.placeId) {
        setReturnOrigin({
          ...destination,
          ...fields,
          isReturn: true,
          type: "origin",
          id: undefined,
        });
      }
      if (origin.placeId) {
        setReturnDestination({
          ...origin,
          ...fields,
          isReturn: true,
          type: "destination",
          id: undefined,
        });
      }
      if (returnIntermediateDestinations.length) {
        setReturnIntermediateDestinations(
          returnIntermediateDestinations.map(
            (returnIntermediateDestination) => ({
              ...returnIntermediateDestination,
              ...fields,
            }),
          ),
        );
      }
      if (returnBoardingPoints.length) {
        setReturnBoardingPoints(
          returnBoardingPoints.map((returnBoardingPoint) => ({
            ...returnBoardingPoint,
            ...fields,
          })),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRoundTrip,
    returnOrigin.placeId,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify([
      ...returnIntermediateDestinations.map(
        (returnIntermediateDestination) =>
          returnIntermediateDestination.placeId,
      ),
      ...returnBoardingPoints.map(
        (returnBoardingPoint) => returnBoardingPoint.placeId,
      ),
    ]),
    returnDestination.placeId,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify([
      origin.placeId,
      ...boardingPoints.map((boardingPoint) => boardingPoint.placeId),
      ...intermediateDestinations.map(
        (intermediateDestination) => intermediateDestination.placeId,
      ),
      destination.placeId,
    ]),
  ]);

  useEffect(() => {
    const locations = [
      origin,
      ...boardingPoints,
      ...intermediateDestinations,
      destination,
    ];
    if (
      locations.every((location) => location.placeId) &&
      origin.departureDate
    ) {
      const _boardingPoints = getBoardingPoints(origin, boardingPoints);
      const _intermediateDestinations = getIntermediateDestinations(
        origin,
        _boardingPoints,
        intermediateDestinations,
      );
      const _destination = getDestination(
        origin,
        _boardingPoints,
        _intermediateDestinations,
        destination,
      );
      setBoardingPoints(_boardingPoints);
      setIntermediateDestinations(_intermediateDestinations);
      setDestination(_destination);
      if (isRoundTrip) {
        const locations = [
          returnOrigin,
          ...returnIntermediateDestinations,
          ...returnBoardingPoints,
          returnDestination,
        ];
        if (locations.every((location) => location.placeId)) {
          const _returnOrigin = { ...returnOrigin };
          _returnOrigin.arrivalDate = _destination.departureDate;
          _returnOrigin.departureDate = _destination.departureDate;
          const _returnIntermediateDestinations = getBoardingPoints(
            _returnOrigin,
            returnIntermediateDestinations,
          );
          const _returnBoardingPoints = getIntermediateDestinations(
            _returnOrigin,
            _returnIntermediateDestinations,
            returnBoardingPoints,
          );
          const _returnDestination = getDestination(
            _returnOrigin,
            _returnIntermediateDestinations,
            _returnBoardingPoints,
            returnDestination,
          );
          setReturnOrigin(_returnOrigin);
          setReturnIntermediateDestinations(_returnIntermediateDestinations);
          setReturnBoardingPoints(_returnBoardingPoints);
          setReturnDestination(_returnDestination);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRoundTrip,
    origin.departureDate,
    destination.departureDate,
    returnOrigin.departureDate,
    returnDestination.departureDate,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify([
      ...boardingPoints.map((boardingPoint) => boardingPoint.departureDate),
      ...intermediateDestinations.map(
        (intermediateDestination) => intermediateDestination.departureDate,
      ),
      ...returnIntermediateDestinations.map(
        (returnIntermediateDestination) =>
          returnIntermediateDestination.departureDate,
      ),
      ...returnBoardingPoints.map(
        (returnBoardingPoint) => returnBoardingPoint.departureDate,
      ),
    ]),
  ]);

  const onAddBoardingPoint = useCallback(
    () =>
      setBoardingPoints([
        ...boardingPoints,
        {
          ...constants.location,
          type: "boardingPoint",
          key: shortid.generate(),
        },
      ]),
    [boardingPoints],
  );

  const onChangeBoardingPoint = useCallback(
    (location) =>
      setBoardingPoints((_boardingPoints) =>
        _boardingPoints.map((boardingPoint) =>
          boardingPoint.key === location.key ? location : boardingPoint,
        ),
      ),
    [],
  );

  const onDeleteBoardingPoint = useCallback(
    (location) => {
      const _boardingPoints = boardingPoints.filter(
        (boardingPoint) => boardingPoint.key !== location.key,
      );
      setBoardingPoints(_boardingPoints);
    },
    [boardingPoints],
  );

  const onAddIntermediateDestination = useCallback(() => {
    setIntermediateDestinations([
      ...intermediateDestinations,
      {
        ...constants.location,
        type: "intermediateDestination",
        key: shortid.generate(),
      },
    ]);
  }, [intermediateDestinations]);

  const onChangeIntermediateDestination = useCallback(
    (location) =>
      setIntermediateDestinations((_intermediateDestinations) =>
        _intermediateDestinations.map((intermediateDestination) =>
          intermediateDestination.key === location.key
            ? location
            : intermediateDestination,
        ),
      ),
    [],
  );

  const onDeleteIntermediateDestination = useCallback(
    (location) => {
      const _intermediateDestinations = intermediateDestinations.filter(
        (intermediateDestination) =>
          intermediateDestination.key !== location.key,
      );
      setIntermediateDestinations(_intermediateDestinations);
    },
    [intermediateDestinations],
  );

  const onAddReturnIntermediateDestination = useCallback(() => {
    setReturnIntermediateDestinations([
      ...returnIntermediateDestinations,
      {
        ...constants.location,
        isReturn: true,
        type: "intermediateDestination",
        key: shortid.generate(),
      },
    ]);
  }, [returnIntermediateDestinations]);

  const onChangeReturnIntermediateDestination = useCallback(
    (location) =>
      setReturnIntermediateDestinations((_returnIntermediateDestinations) =>
        _returnIntermediateDestinations.map((returnIntermediateDestination) =>
          returnIntermediateDestination.key === location.key
            ? location
            : returnIntermediateDestination,
        ),
      ),
    [],
  );

  const onDeleteReturnIntermediateDestination = useCallback(
    (location) => {
      const _returnIntermediateDestinations =
        returnIntermediateDestinations.filter(
          (returnIntermediateDestination) =>
            returnIntermediateDestination.key !== location.key,
        );
      setReturnIntermediateDestinations(_returnIntermediateDestinations);
    },
    [returnIntermediateDestinations],
  );

  const onAddReturnBoardingPoints = useCallback(
    () =>
      setReturnBoardingPoints([
        ...returnBoardingPoints,
        {
          ...constants.location,
          isReturn: true,
          type: "boardingPoint",
          key: shortid.generate(),
        },
      ]),
    [returnBoardingPoints],
  );

  const onChangeReturnBoardingPoint = useCallback(
    (location) =>
      setReturnBoardingPoints((_returnBoardingPoints) =>
        _returnBoardingPoints.map((returnBoardingPoint) =>
          returnBoardingPoint.key === location.key
            ? location
            : returnBoardingPoint,
        ),
      ),
    [],
  );

  const onDeleteReturnBoardingPoint = useCallback(
    (location) => {
      const _returnBoardingPoints = returnBoardingPoints.filter(
        (returnBoardingPoint) => returnBoardingPoint.key !== location.key,
      );
      setReturnBoardingPoints(_returnBoardingPoints);
    },
    [returnBoardingPoints],
  );

  const onContinue = async () => {
    try {
      setIsSaving(true);
      const itinerary = [
        { ...origin, arrivalDate: origin.departureDate },
        ...boardingPoints,
        ...intermediateDestinations,
        {
          ...destination,
          departureDate: isRoundTrip
            ? destination.departureDate
            : destination.arrivalDate,
        },
      ].concat(
        isRoundTrip
          ? [
              returnOrigin,
              ...returnIntermediateDestinations,
              ...returnBoardingPoints,
              returnDestination,
            ]
          : [],
      );
      if (
        itinerary.some(
          (location) => !location.placeId || !location.departureDate,
        )
      ) {
        status.updateWarningMessage("Seleccione una fecha de salida");
        setIsSaving(false);
        return;
      }
      const data = {
        user: state.user.id,
        agency: state.user?.agency?.id,
        numberOfPassengers,
        itinerary,
        polyline,
        returnPolyline,
        holder,
      };
      let quotationId = params.id;
      if (quotationId) {
        await API(state.jwt).put(`/quotations/${quotationId}`, { data });
      } else {
        const {
          data: { data: quotation },
        } = await API(state.jwt).post("/quotations", { data });
        quotationId = quotation.id;
      }
      navigate(routes.agencyExperienceCreationId.replace(":id", quotationId), {
        replace: true,
      });
      navigate(
        routes.agencyExperienceCreationDetails.replace(":id", quotationId),
      );
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      status.updateErrorMessage(error);
    }
  };

  return (
    <VStack>
      <Helmet>
        <title>Creación de experiencia</title>
      </Helmet>
      <HStack paddingBottom={8} space={4} zIndex={1}>
        <Heading color={colors.secondary} fontSize="2xl">
          Empezemos a
          <br />
          crear tu viaje
        </Heading>
        <Pressable
          backgroundColor={colors.secondary}
          paddingX={4}
          paddingY={2}
          borderRadius={5}
          onPress={() => mapContainer.current.scrollIntoView()}>
          <HStack space={2}>
            <FiMap color={colors.white} size={20} />
            <Text color={colors.white}>Ver en mapa</Text>
          </HStack>
        </Pressable>
        <Holder isLoading={isLoading} holder={holder} setHolder={setHolder} />
      </HStack>
      <VStack space={8} paddingBottom={10}>
        <VStack backgroundColor={colors.white} padding={8} zIndex={1}>
          <VStack zIndex={1}>
            <Text bold color={colors.primary} fontSize="xl">
              Ida
            </Text>
            <Divider backgroundColor={colors.primary} height={1} marginY={3} />
            {isLoading ? (
              <Center height={400}>
                <Spinner size="lg" color={colors.primary} />
              </Center>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <Cell header color={colors.third} />
                    <Cell header color={colors.third} />
                    <Cell header color={colors.third}>
                      Fecha de llegada
                    </Cell>
                    <Cell header color={colors.third}>
                      Hora de llegada
                    </Cell>
                    <Cell header color={colors.third}>
                      Estancia
                    </Cell>
                    <Cell header color={colors.third}>
                      Fecha de salida
                    </Cell>
                    <Cell header color={colors.third}>
                      Hora de salida
                    </Cell>
                    <Cell header color={colors.third}>
                      Observaciones
                    </Cell>
                  </tr>
                </thead>
                <tbody>
                  <Location
                    title="Origen"
                    numeral={1}
                    location={origin}
                    onChange={setOrigin}
                    hideArrivalDate
                    hideStay
                    forceDepartureDate={Boolean(origin.placeId)}
                  />
                  {boardingPoints.map((boardingPoint, index) => (
                    <Location
                      key={boardingPoint.key}
                      title={`Punto de abordaje #${index + 1}`}
                      numeral={index + 2}
                      location={boardingPoint}
                      onChange={onChangeBoardingPoint}
                      onDelete={onDeleteBoardingPoint}
                    />
                  ))}
                  <AddLocation
                    label="Añadir punto de abordaje"
                    onPress={onAddBoardingPoint}
                  />
                  {intermediateDestinations.map(
                    (intermediateDestination, index) => (
                      <Location
                        key={intermediateDestination.key}
                        title={`Destino intermedio #${index + 1}`}
                        numeral={boardingPoints.length + index + 2}
                        location={intermediateDestination}
                        onChange={onChangeIntermediateDestination}
                        onDelete={onDeleteIntermediateDestination}
                      />
                    ),
                  )}
                  <AddLocation
                    label="Añadir destino intermedio"
                    onPress={onAddIntermediateDestination}
                  />
                  <Location
                    title="Destino final"
                    numeral={
                      boardingPoints.length +
                      intermediateDestinations.length +
                      2
                    }
                    location={destination}
                    onChange={setDestination}
                    isLast
                    readOnlyDepartureDate
                  />
                  {isRoundTrip && (
                    <>
                      <tr className={styles.roundTrip}>
                        <Cell bold color={colors.primary} fontSize="xl">
                          Vuelta
                        </Cell>
                        {Array.from(new Array(8)).map((_, index) => (
                          <Cell key={String(index)} />
                        ))}
                      </tr>
                      <Location
                        title="Origen"
                        numeral={1}
                        location={returnOrigin}
                        onChange={(location) =>
                          setDestination((_destination) => ({
                            ..._destination,
                            departureDate: location.departureDate,
                          }))
                        }
                        hideArrivalDate
                        hideStay
                        disabled
                        clearDisabled
                      />
                      {returnIntermediateDestinations.map(
                        (returnIntermediateDestination, index) => (
                          <Location
                            key={returnIntermediateDestination.key}
                            title={`Destino intermedio #${index + 1}`}
                            numeral={index + 2}
                            location={returnIntermediateDestination}
                            onChange={onChangeReturnIntermediateDestination}
                            onDelete={onDeleteReturnIntermediateDestination}
                          />
                        ),
                      )}
                      <AddLocation
                        label="Añadir destino intermedio"
                        onPress={onAddReturnIntermediateDestination}
                      />
                      {returnBoardingPoints.map(
                        (returnBoardingPoint, index) => (
                          <Location
                            key={returnBoardingPoint.key}
                            title={`Punto de descenso #${index + 1}`}
                            numeral={returnBoardingPoints.length + index + 2}
                            location={returnBoardingPoint}
                            onChange={onChangeReturnBoardingPoint}
                            onDelete={onDeleteReturnBoardingPoint}
                          />
                        ),
                      )}
                      <AddLocation
                        label="Añadir punto de descenso"
                        onPress={onAddReturnBoardingPoints}
                      />
                      <Location
                        title="Final de viaje"
                        numeral={
                          returnBoardingPoints.length +
                          returnIntermediateDestinations.length +
                          2
                        }
                        location={returnDestination}
                        onChange={setReturnDestination}
                        isLast
                        hideDepartureDate
                        hideStay
                        disabled
                        clearDisabled
                      />
                    </>
                  )}
                </tbody>
              </table>
            )}
          </VStack>
        </VStack>
        <HStack backgroundColor={colors.white} padding={8} space={70}>
          <Radio.Group
            accessibilityLabel="is round trip"
            value={isRoundTrip}
            onChange={(value) => {
              setIsRoundTrip(value);
            }}>
            <HStack space={5}>
              <Radio
                colorScheme="danger"
                value={true}
                isDisabled={isLoading || isSaving}>
                Ida y vuelta (viaje redondo)
              </Radio>
              <Radio
                colorScheme="danger"
                value={false}
                isDisabled={isLoading || isSaving}>
                Solo ida (viaje sencillo)
              </Radio>
            </HStack>
          </Radio.Group>
          <HStack flex={1} space={5}>
            <Heading fontSize="lg">
              Número de
              <br />
              Pasajeros
            </Heading>
            <HStack space={4}>
              <Image src={seat} alt="seat" width={7} height={7} />
              <Pressable
                onPress={() =>
                  numberOfPassengers > 1 &&
                  setNumberOfPassengers(numberOfPassengers - 1)
                }
                disabled={isLoading || isSaving}>
                <Image
                  src={removeCircle}
                  alt="remove-circle"
                  width={5}
                  height={5}
                />
              </Pressable>
              <Input
                variant="unstyled"
                color={colors.gray}
                fontSize="2xl"
                value={numberOfPassengers}
                onChangeText={(text) => setNumberOfPassengers(Number(text))}
                width={20}
                textAlign="center"
              />
              <Pressable
                onPress={() => setNumberOfPassengers(numberOfPassengers + 1)}
                disabled={isLoading || isSaving}>
                <Image src={addCircle} alt="add-circle" width={5} height={5} />
              </Pressable>
            </HStack>
          </HStack>
          <Pressable
            disabled={
              isLoading || isSaving || noResults || numberOfPassengers < 1
            }
            onPress={onContinue}>
            <Box
              backgroundColor={colors.primary}
              paddingY={3}
              paddingX={100}
              borderRadius="full">
              <Text color={colors.white}>
                {params.id ? "Guardar" : "Continuar"}
              </Text>
            </Box>
          </Pressable>
        </HStack>
        <VStack ref={mapContainer}>
          {isRoundTrip && (
            <Text bold color={colors.primary} fontSize="3xl">
              Ida
            </Text>
          )}
          <Box height={400} width="full">
            <Map setRef={setMap}>
              {Boolean(polyline) && (
                <Polyline
                  path={decodePolyline(polyline)}
                  options={{
                    strokeColor: colors.primary,
                  }}
                />
              )}
              {Boolean(origin.placeId) && (
                <Marker
                  position={{ lat: origin.latitude, lng: origin.longitude }}
                  icon={mapIcons.origin}
                  title={origin.name}
                />
              )}
              {Boolean(destination.placeId) && (
                <Marker
                  position={{
                    lat: destination.latitude,
                    lng: destination.longitude,
                  }}
                  icon={mapIcons.destination}
                  title={destination.name}
                />
              )}
              {boardingPoints
                .filter((boardingPoint) => boardingPoint.placeId)
                .map((boardingPoint) => (
                  <Marker
                    key={boardingPoint.placeId}
                    position={{
                      lat: boardingPoint.latitude,
                      lng: boardingPoint.longitude,
                    }}
                    icon={mapIcons.boardingPoint}
                    title={boardingPoint.name}
                  />
                ))}
              {intermediateDestinations
                .filter(
                  (intermediateDestination) => intermediateDestination.placeId,
                )
                .map((intermediateDestination) => (
                  <Marker
                    key={intermediateDestination.placeId}
                    position={{
                      lat: intermediateDestination.latitude,
                      lng: intermediateDestination.longitude,
                    }}
                    icon={mapIcons.intermediateDestination}
                    title={intermediateDestination.name}
                  />
                ))}
            </Map>
          </Box>
        </VStack>
        {isRoundTrip && (
          <VStack>
            <Text bold color={colors.primary} fontSize="3xl">
              Vuelta
            </Text>
            <Box height={400} width="full">
              <Map setRef={setReturnMap}>
                {Boolean(returnPolyline) && (
                  <Polyline
                    path={decodePolyline(returnPolyline)}
                    options={{
                      strokeColor: colors.primary,
                    }}
                  />
                )}
                {Boolean(returnOrigin.placeId) && (
                  <Marker
                    position={{
                      lat: returnOrigin.latitude,
                      lng: returnOrigin.longitude,
                    }}
                    icon={mapIcons.origin}
                    title={returnOrigin.name}
                  />
                )}
                {returnIntermediateDestinations
                  .filter(
                    (returnIntermediateDestination) =>
                      returnIntermediateDestination.placeId,
                  )
                  .map((returnIntermediateDestination) => (
                    <Marker
                      key={returnIntermediateDestination.placeId}
                      position={{
                        lat: returnIntermediateDestination.latitude,
                        lng: returnIntermediateDestination.longitude,
                      }}
                      icon={mapIcons.intermediateDestination}
                      title={returnIntermediateDestination.name}
                    />
                  ))}
                {returnBoardingPoints
                  .filter((returnBoardingPoint) => returnBoardingPoint.placeId)
                  .map((returnBoardingPoint) => (
                    <Marker
                      key={returnBoardingPoint.placeId}
                      position={{
                        lat: returnBoardingPoint.latitude,
                        lng: returnBoardingPoint.longitude,
                      }}
                      icon={mapIcons.boardingPoint}
                      title={returnBoardingPoint.name}
                    />
                  ))}
                {Boolean(returnDestination.placeId) && (
                  <Marker
                    position={{
                      lat: returnDestination.latitude,
                      lng: returnDestination.longitude,
                    }}
                    icon={mapIcons.destination}
                    title={returnDestination.name}
                  />
                )}
              </Map>
            </Box>
          </VStack>
        )}
      </VStack>
      <Vehicles />
      <QuotationFinishedModal isOpen={finished} />
      <NoResultsModal
        isOpen={isNoResultsModalOpen}
        onRequestClose={onCloseNoResultsModalOpen}
      />
    </VStack>
  );
}

export default AgencyExperienceCreationPage;
